import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Typography, Grid, Box, Container, Card, CardHeader, Divider, CardContent, Hidden, IconButton, Toolbar, TextField, Checkbox, ListItemText, MenuItem, Dialog, DialogTitle, DialogContent, Alert, DialogActions, Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import { connect } from 'react-redux';
import {
  Truck as TruckIcon,
  Edit as EditIcon
} from 'react-feather';
import GlobalSearch from '../components/GlobalSearch';
import FilterBy from '../components/FilterBy';
import Table from '../components/Table';
import MobileDataList from '../components/MobileDataList';
import AlertIndicatorIcon from '../components/AlertIndicatorIcon';
import AppConstants from '../constants/AppConstants';
import { refreshData, reset, searchByText, searchByWarning, changePage, changeSortModel, changeFilterByColumn, selectStates } from '../store/actions/vehicleIssuesActions';
import FormattedVehicle from '../models/FormattedVehicle';
import { RootState } from '../store/store';
import EnumService from '../services/EnumService';
import { EnumRes, VehicleIssueRes } from '../proxy/proxy';
import MultiRowActionButton from '../components/MultiRowActionButton';
import { showAlert as showGlobalAlert, clearAllAlerts as clearAllGlobalAlerts } from '../store/actions/alertActions';
import { hide, show } from '../store/actions/busyIndicatorActions';
import DateRangeSelector from '../components/DateRangeSelector';
import { changeDateRange } from '../store/actions/shiftActions';
import LocalStorageService from '../services/LocalStorageService';
import { isDriver } from '../utils/common';

interface Props {
  startDate: string,
  endDate: string,
  visibleRows: Array<FormattedVehicle>,
  selectedRowIds: Array<number>,
  totalRowCount: number,
  searchedText: string,
  selectedWarning: string,
  currentPage: number,
  sortModel: any,
  selectedStates: Array<number>,
  filterByCol: any,
  fetchAllRows: any,
  resetPage: any,
  filterByText: any,
  filterByWarning: any,
  onPageChange: any,
  onSort: any,
  onChangeFilterByColumn: any,
  onDateRangeChange: (startDate: string, endDate: string) => any,
  setStates: (states: Array<number>) => void,
  showAlert: (message: any) => any,
  clearAllAlerts: () => any,
  showBusyIndicator: () => any,
  hideBusyIndicator: () => any,
}

const VehicleIssues: React.FC<Props> = ({
  startDate,
  endDate,
  visibleRows,
  totalRowCount,
  selectedRowIds,
  searchedText,
  selectedWarning,
  currentPage,
  sortModel,
  filterByCol,
  selectedStates,
  fetchAllRows,
  resetPage,
  filterByText,
  filterByWarning,
  onPageChange,
  onSort,
  onChangeFilterByColumn,
  setStates,
  showAlert,
  clearAllAlerts,
  showBusyIndicator,
  hideBusyIndicator,
  onDateRangeChange
}) => {
  const navigate = useNavigate();
  const [allVehicleIssueStates, setAllVehicleIssueStates] = useState<EnumRes[]>([]);
  const [openDeleteWarning, setOpenDeleteWarning] = React.useState(false);
  const localStorageService = LocalStorageService.getService();
  const roles = localStorageService.getUserRoles();

  const onRowClick = ({ id, shift }: any) => {
    navigate(`/${isDriver(roles) ? 'employee' : 'app'}/vehicle/issues/${id}/${shift?.id || 'standalone'}`);
  };

  const handleDateRangeChange = (start: string, end: string) => {
    onDateRangeChange(start, end);
    if (start && end) {
      fetchAllRows(start, end);
    }
  };

  const getIssueState = (id: number) =>
    allVehicleIssueStates.find((x) => x.id === id)?.value || '--';

  const columns: Array<any> = [
    {
      field: 'registrationNumber',
      headerName: 'Registration Number',
      width: 250,
      renderCell: (params: any) => {
        const { row } = params;
        return <span>{row.vehicle?.registrationNumber || '--'}</span>;
      },
    },
    {
      field: 'summary',
      headerName: 'Summary',
      flex: 1,
    },
    {
      field: 'actionTaken',
      headerName: 'Action Taken',
      width: 250,
    },
    {
      field: 'actionTakenOnUtc',
      headerName: 'Action Taken On',
      width: 200,
      renderCell: (params: any) => {
        const { value } = params;
        return <span>{value ? new Date(value).toLocaleString() : '--'}</span>;
      },
    },
    {
      field: 'vehicleIssueStatus',
      headerName: 'Issue Status',
      width: 200,
      renderCell: (params: any) => {
        const { value } = params;
        return <span>{getIssueState(value)}</span>;
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'number',
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        const { row } = params;
        return (
          <IconButton size="medium" aria-label="edit" onClick={() => onRowClick(row)}>
            <EditIcon />
          </IconButton>
        );
      },
      renderHeader: () => <span />,
    },
  ];

  // const getIssueStatus = () => {
  //   return '';
  // }

  const onSearchTextChange = (event: any) => {
    filterByText(event?.target?.value);
  };

  const refresh = () => {
    resetPage();
    fetchAllRows();
  };

  useEffect(() => {
    EnumService.getEnums('vehicleIssueStatuses').then((states) => {
      setAllVehicleIssueStates(states);
      fetchAllRows();
    }).catch();
  }, []);

  const reportVehicleIssue = () => {
    navigate(`/${isDriver(roles) ? 'employee' : 'app'}/vehicle/issues/add/standalone`);
  };

  const renderEachMobileCard = (row: VehicleIssueRes) => (
    <Card key={row.id} sx={{ marginBottom: '10px' }} variant="outlined">
      <CardHeader
        avatar={
          <TruckIcon />
        }
        action={
          (
            <>
              <IconButton aria-label="edit">
                <EditIcon />
              </IconButton>
            </>
          )
        }
        title={row.vehicle?.registrationNumber}
        onClick={() => onRowClick(row)}
      />
      <Divider />
      <CardContent>
        <Grid container>
          {row.summary && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <b>Summary:</b> {row.summary}
              </Typography>
            </Grid>
          )}
          {row.actionTaken && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <b>Action Taken:</b> {row.actionTaken}
              </Typography>
            </Grid>
          )}
          {row.actionTakenOnUtc && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <b>Action Taken On:</b> {new Date(row.actionTakenOnUtc).toLocaleString()}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>Issue Status:</b> {getIssueState(row.vehicleIssueStatus || 0)}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <>
      <Helmet>
        <title>Issues | {AppConstants.APP_NAME}</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <Container maxWidth="xl">
          <Card>
            <CardHeader
              action={
                (
                  <>
                    <IconButton onClick={refresh}>
                      <RefreshIcon />
                    </IconButton>
                    <IconButton onClick={reportVehicleIssue}>
                      <AddIcon />
                    </IconButton>
                  </>
                )
              }
              title="Vehicles"
            />
            <Divider />
            <CardContent>
              <Hidden mdDown>
                <Toolbar style={{ justifyContent: 'flex-end' }}>
                  <DateRangeSelector id="shifts" startDate={startDate} endDate={endDate} allowFutureDates onChange={handleDateRangeChange} />
                  <div style={{ flex: '1' }} />
                  <GlobalSearch searchedText={searchedText} onSearchTextChange={onSearchTextChange} showFilterByColumn columns={columns} filterByColumn={filterByCol} onFilterByColumnChange={onChangeFilterByColumn} width="400px" />
                </Toolbar>
                <Table
                  rows={visibleRows}
                  columns={columns}
                  onRowDoubleClick={onRowClick}
                  totalRows={totalRowCount}
                  page={currentPage}
                  onPageChange={onPageChange}
                  sortModel={sortModel}
                  checkboxSelection={false}
                  onSortModelChange={onSort}
                  selectionModel={visibleRows?.filter((row: any) => row.selected).map((row) => row.id) ?? []}
                />
              </Hidden>
              <Hidden mdUp>
                <Toolbar sx={{ flexDirection: 'column' }}>
                  <div style={{ width: '100%', padding: '5px 0' }}>
                    <DateRangeSelector id="shifts" startDate={startDate} endDate={endDate} allowFutureDates onChange={handleDateRangeChange} />
                    <GlobalSearch searchedText={searchedText} onSearchTextChange={onSearchTextChange} showFilterByColumn columns={columns} filterByColumn={filterByCol} onFilterByColumnChange={onChangeFilterByColumn} />
                  </div>
                </Toolbar>
                <MobileDataList
                  rows={visibleRows}
                  renderEachRow={renderEachMobileCard}
                  totalRows={totalRowCount}
                  page={currentPage}
                  onPageChange={onPageChange}
                />
              </Hidden>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  startDate: state.shift?.startDate,
  endDate: state.shift?.endDate,
  visibleRows: state.vehicleIssues?.filteredRows,
  selectedRowIds: state.vehicleIssues?.allRows?.filter((row: any) => row.selected).map((row) => row.id ?? 0) ?? [],
  totalRowCount: state.vehicleIssues?.allRows?.length,
  currentPage: state.vehicleIssues?.currentPage,
  searchedText: state.vehicleIssues?.searchedText,
  selectedWarning: state.vehicleIssues?.filterBy,
  sortModel: state.vehicleIssues?.sortModel,
  filterByCol: state.vehicleIssues?.filterByCol,
  selectedStates: state.vehicleIssues.states ?? [],
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchAllRows: (startDate: string, endDate: string) => dispatch(refreshData(startDate, endDate)),
  resetPage: () => dispatch(reset()),
  filterByText: (text: string) => dispatch(searchByText(text)),
  filterByWarning: (text: string) => dispatch(searchByWarning(text)),
  onPageChange: (page: number) => dispatch(changePage(page)),
  onSort: (sortModel: any) => dispatch(changeSortModel(sortModel)),
  setStates: (states: Array<number>) => dispatch(selectStates(states)),
  onChangeFilterByColumn: (col: string) => dispatch(changeFilterByColumn(col)),
  showBusyIndicator: () => dispatch(show()),
  hideBusyIndicator: () => dispatch(hide()),
  onDateRangeChange: (startDate: string, endDate: string) => dispatch(changeDateRange(startDate, endDate)),
  showAlert: (message: any) => dispatch(showGlobalAlert(message)),
  clearAllAlerts: () => dispatch(clearAllGlobalAlerts())
});

export default connect(mapStateToProps, mapDispatchToProps)(VehicleIssues);
